import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, VideoCamera, Stop, Play, Pause, Trash, ArrowsClockwise,
  Lock, Sparkle, Check, Info, Camera, X, Share, DownloadSimple,
} from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import SwingCoach from "../components/SwingCoach";

/**
 * Swing Lab (Pro) — MVP.
 *
 * What it does today
 * ------------------
 *   • Records a swing through the device's back camera at the highest
 *     framerate the browser will give us (typically 30-60 fps; iPhones with
 *     Safari can hit 240 fps slow-mo but only via native APIs — out of
 *     scope for the PWA right now).
 *   • Playback with frame-by-frame ← / → scrubbing for slow-mo analysis.
 *   • Auto-tempo estimate: tracks `currentTime` of the markers the user taps
 *     for "top of backswing" + "impact" — backswing:downswing ratio is the
 *     gold-standard tempo metric (PGA pros average 3 : 1).
 *   • Saves up to 5 recent swings in browser IndexedDB (no server cost).
 *
 * What's intentionally NOT in the MVP
 * ----------------------------------
 *   • Pose skeleton overlay — needs TensorFlow.js, adds ~6 MB to the bundle,
 *     ship later if users love the basic feature first.
 *   • Ball-speed-from-video — needs higher fps than browsers reliably give us.
 *   • Cloud upload / sharing — local-only keeps it free + private.
 */
const MAX_SWINGS = 5;
const DB_NAME = "fairway-swings";
const STORE = "swings";

// Tiny IndexedDB wrapper — small enough not to be worth a dep.
function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE, { keyPath: "id" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function loadAllSwings() {
  const db = await openDb();
  return new Promise((res) => {
    const tx = db.transaction(STORE, "readonly").objectStore(STORE).getAll();
    tx.onsuccess = () => res((tx.result || []).sort((a, b) => b.created_at - a.created_at));
    tx.onerror = () => res([]);
  });
}
async function saveSwing(rec) {
  const db = await openDb();
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE, "readwrite").objectStore(STORE).put(rec);
    tx.onsuccess = () => res();
    tx.onerror = () => rej(tx.error);
  });
}
async function deleteSwing(id) {
  const db = await openDb();
  return new Promise((res) => {
    const tx = db.transaction(STORE, "readwrite").objectStore(STORE).delete(id);
    tx.onsuccess = () => res();
    tx.onerror = () => res();
  });
}

export default function SwingLab() {
  const { user } = useAuth();
  const isPro =
    user?.role === "admin" ||
    ["active", "trialing"].includes(user?.subscription_status);

  // ──────────── Pro gate ────────────
  if (!isPro) return <ProGate />;

  return <SwingLabInner />;
}

function ProGate() {
  return (
    <div className="px-5 pt-6 pb-8">
      <Link to="/app" className="btn-ghost -ml-2 mb-3" data-testid="swing-lab-back">
        <ArrowLeft size={20} weight="bold" />
      </Link>
      <p className="label-uppercase">Coaching</p>
      <h1 className="font-heading font-black tracking-tighter-2 text-4xl text-[var(--primary)] mb-2">
        Swing Lab
      </h1>
      <p className="text-sm text-[var(--text-secondary)] mb-6">
        Prop your phone up on the bag, tap record, take your swing — review it frame-by-frame, measure your tempo, and track your improvement over time.
      </p>
      <div className="surface-card p-5 mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Sparkle size={18} weight="fill" className="text-[var(--accent-terracotta)]" />
          <h3 className="font-heading font-bold text-[var(--primary)]">Pro unlocks</h3>
        </div>
        <ul className="space-y-1.5 text-sm">
          {[
            "Record swings through your phone's back camera",
            "Frame-by-frame slow-mo playback",
            "AI swing analysis — head sway, hip slide, shoulder turn",
            "Tap-and-track tempo (back-swing : down-swing ratio)",
            "Save your last 5 swings locally — totally private",
          ].map((p) => (
            <li key={p} className="flex items-start gap-2">
              <Check size={14} weight="bold" className="text-[var(--primary)] shrink-0 mt-0.5" />
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </div>
      <Link to="/subscribe" className="btn-primary w-full" data-testid="swing-lab-go-pro">
        <Lock size={16} weight="bold" /> Go Pro to unlock
      </Link>
    </div>
  );
}

function SwingLabInner() {
  const nav = useNavigate();
  const [mode, setMode] = useState("idle"); // idle | recording | review
  const [swings, setSwings] = useState([]);
  const [activeSwing, setActiveSwing] = useState(null);
  const [error, setError] = useState("");
  // Setup-tips card: open the first time, dismissible per device.
  const [showTips, setShowTips] = useState(() => {
    try { return localStorage.getItem("swingLabTipsDismissed") !== "1"; }
    catch { return true; }
  });
  const dismissTips = () => {
    try { localStorage.setItem("swingLabTipsDismissed", "1"); } catch (_e) { /* localStorage may be disabled */ }
    setShowTips(false);
  };
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const videoRef = useRef(null);
  const startedAtRef = useRef(0);

  useEffect(() => {
    loadAllSwings().then(setSwings);
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  const errorRef = useRef(null);
  const showError = (msg) => {
    setError(msg);
    // Scroll the error into view so the user actually sees it — the Swing
    // Lab landing is long enough that the user is usually scrolled past the
    // header when they tap "Record a swing".
    requestAnimationFrame(() => {
      errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  const startRecording = async () => {
    setError("");
    // Pre-flight: spell out the common iOS / old-browser fail modes BEFORE
    // we try to grab the camera, so users see a clear message instead of a
    // silent no-op.
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      showError(
        "Your browser can't access the camera. Open Fairway in Safari or Chrome (not in an in-app browser like Instagram / Facebook).",
      );
      return;
    }
    if (typeof window.MediaRecorder === "undefined") {
      showError(
        "Video recording isn't supported on this browser. iPhones need iOS 14.5 or newer; older Androids may need Chrome.",
      );
      return;
    }
    if (!window.isSecureContext) {
      showError("The camera only works on https — make sure you're on https://fairway-golf.org.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 60 },
        },
        audio: false,
      });
      streamRef.current = stream;

      // Flip mode FIRST so the <video> element actually mounts. Trying to
      // assign srcObject before this means videoRef.current is still null
      // (React hasn't rendered the recording view yet) and the user gets a
      // black preview even though the OS-level camera-in-use indicator is on.
      setMode("recording");
      // Wait one paint so React commits the DOM and videoRef is wired up.
      // Two rAFs to be extra safe on slow mid-range Androids.
      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => requestAnimationFrame(r));

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // Mobile Chrome won't autoplay a freshly-attached MediaStream even
        // with muted+playsInline unless we explicitly call .play() inside
        // the same user-gesture chain. Catch + log so the recording still
        // starts even if the preview fails (rare).
        try {
          await videoRef.current.play();
        } catch (playErr) {
          console.warn("SwingLab: video preview .play() rejected", playErr);
        }
        // Belt-and-braces for older phones: wait until the video element
        // reports a non-zero size before starting MediaRecorder, so the
        // user doesn't see "REC indicator on + black preview" if the
        // browser stalls. Cap the wait at 1.5s so we never block forever.
        await new Promise((resolve) => {
          const v = videoRef.current;
          if (!v) return resolve();
          if (v.videoWidth > 0) return resolve();
          const timeout = setTimeout(resolve, 1500);
          const onReady = () => { clearTimeout(timeout); resolve(); };
          v.addEventListener("loadeddata", onReady, { once: true });
        });
      }

      // Pick the best supported video codec — MP4/H.264 ideal for cross-device
      // playback, fall back to WebM/VP9 (modern Android Chrome).
      const candidates = ["video/mp4;codecs=avc1", "video/webm;codecs=vp9", "video/webm"];
      const mimeType = candidates.find((c) => window.MediaRecorder?.isTypeSupported?.(c)) || "";
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "video/webm" });
        const id = `swing-${Date.now()}`;
        const rec = {
          id,
          blob,
          created_at: Date.now(),
          duration_ms: Date.now() - startedAtRef.current,
          mime: blob.type,
        };
        await saveSwing(rec);
        const all = await loadAllSwings();
        // Prune to MAX_SWINGS.
        for (const old of all.slice(MAX_SWINGS)) await deleteSwing(old.id);
        const fresh = await loadAllSwings();
        setSwings(fresh);
        setActiveSwing(rec);
        setMode("review");
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      };
      recorderRef.current = recorder;
      startedAtRef.current = Date.now();
      recorder.start();
    } catch (e) {
      console.error("SwingLab: startRecording failed", e);
      // Decode the common DOMException names into a human message so the
      // user knows WHY recording didn't open, not just that it didn't.
      let msg;
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        msg = "Camera access was blocked. On iPhone: Settings → Safari → Camera → Allow. On Android Chrome: tap the padlock in the address bar → Permissions → Camera → Allow.";
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        msg = "No camera was found on this device.";
      } else if (name === "NotReadableError" || name === "TrackStartError") {
        msg = "Your camera is already in use by another app (Camera / FaceTime / Zoom). Close it and tap Record again.";
      } else if (name === "OverconstrainedError") {
        msg = "Your camera can't match the requested settings. Try a different device or browser.";
      } else if (name === "SecurityError") {
        msg = "Camera blocked by browser security settings. Make sure Fairway is opened on https://fairway-golf.org (not embedded in another app).";
      } else {
        msg = e?.message || "Couldn't access the camera. Check browser permissions and try again.";
      }
      showError(msg);
      setMode("idle");
      // Tidy up any half-acquired stream so the camera light doesn't stay on.
      if (streamRef.current) {
        try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch (_e) { /* no-op */ }
        streamRef.current = null;
      }
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  };

  const openSwing = (s) => { setActiveSwing(s); setMode("review"); };
  const closeReview = () => { setActiveSwing(null); setMode("idle"); };

  return (
    <div className="px-5 pt-6 pb-8" data-testid="swing-lab">
      <button onClick={() => nav("/app")} className="btn-ghost -ml-2 mb-3" data-testid="swing-lab-back">
        <ArrowLeft size={20} weight="bold" />
      </button>
      <p className="label-uppercase">Coaching</p>
      <h1 className="font-heading font-black tracking-tighter-2 text-4xl text-[var(--primary)] mb-1">
        Swing Lab
      </h1>
      <p className="text-sm text-[var(--text-secondary)] mb-5">
        Prop your phone on the bag, frame yourself side-on, tap record.
      </p>

      {/* AI PGA-coach analysis card — sits above the existing self-record
          flow so it's discoverable. Works on your OWN swing or a mate's. */}
      <div className="surface-card mb-5">
        <SwingCoach />
      </div>

      {error && (
        <div
          ref={errorRef}
          data-testid="swing-lab-error"
          className="text-sm text-[var(--status-bad)] bg-red-50 border-2 border-red-300 rounded-xl px-4 py-3 mb-3 shadow-md"
          role="alert"
        >
          <div className="font-bold mb-1">Couldn&apos;t start recording</div>
          <div className="leading-snug">{error}</div>
        </div>
      )}

      {mode === "recording" ? (
        <div data-testid="swing-lab-recording">
          <div className="relative rounded-2xl overflow-hidden bg-black aspect-[3/4]">
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              playsInline
              // Old iOS Safari (< 13) and WKWebView need the legacy attribute;
              // modern Safari ignores it. Harmless on Chrome/Android. Use the
              // suppressHydrationWarning bracket-notation to bypass React's
              // unknown-prop warning while still serialising the attribute.
              {...{ "webkit-playsinline": "true" }}
              muted
              autoPlay
              onLoadedMetadata={(e) => {
                // Some older Androids drop the first .play() from the gesture
                // chain but fire reliably once metadata is loaded. Re-trigger
                // play here as a no-op safety net.
                e.currentTarget.play().catch((err) => {
                  console.warn("SwingLab: loadedmetadata replay failed", err);
                });
              }}
            />
            <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-[var(--accent-terracotta)] text-white text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full">
              <span className="h-2 w-2 rounded-full bg-white animate-pulse" /> Rec
            </div>

            {/* Range-mode quick-tip pill (top right). Tap to expand → range-
                specific framing reminder. Auto-collapses after 6s so it
                doesn't get in the way of the live preview. */}
            <RangeTipPill />

            <div className="absolute bottom-3 left-3 right-3 text-center text-white/90 text-[11px] bg-black/40 backdrop-blur-sm rounded-full py-1.5 px-3 leading-tight">
              Frame <strong className="text-white">head + feet</strong> · landscape · prop the phone on your bag
            </div>
          </div>
          <button
            type="button"
            onClick={stopRecording}
            data-testid="swing-lab-stop"
            className="btn-primary w-full mt-3"
            style={{ background: "var(--accent-terracotta)" }}
          >
            <Stop size={18} weight="fill" /> Stop recording
          </button>
        </div>
      ) : mode === "review" && activeSwing ? (
        <SwingReview swing={activeSwing} onClose={closeReview} onDeleted={async () => {
          await deleteSwing(activeSwing.id);
          const fresh = await loadAllSwings();
          setSwings(fresh);
          closeReview();
        }} />
      ) : (
        <>
          {showTips ? (
            <SetupTipsCard onDismiss={dismissTips} />
          ) : (
            <button
              type="button"
              onClick={() => setShowTips(true)}
              data-testid="swing-lab-tips-reopen"
              className="text-xs font-bold text-[var(--primary)] underline-offset-2 hover:underline mb-3 inline-flex items-center gap-1"
            >
              <Info size={14} weight="bold" /> Show setup tips
            </button>
          )}
          <button
            type="button"
            onClick={startRecording}
            data-testid="swing-lab-record"
            className="btn-primary w-full"
          >
            <VideoCamera size={18} weight="fill" /> Record a swing
          </button>

          {swings.length > 0 && (
            <div className="mt-6">
              <p className="label-uppercase mb-2">Recent swings</p>
              <div className="space-y-2">
                {swings.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => openSwing(s)}
                    data-testid={`swing-lab-recent-${s.id}`}
                    className="w-full text-left surface-card p-3 flex items-center gap-3 hover:shadow-md transition-shadow"
                  >
                    <div className="h-10 w-10 rounded-xl bg-[var(--primary)] text-white flex items-center justify-center">
                      <Play size={16} weight="fill" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-bold text-sm text-[var(--primary)]">
                        {new Date(s.created_at).toLocaleString()}
                      </div>
                      <div className="text-[11px] text-[var(--text-secondary)]">
                        {(s.duration_ms / 1000).toFixed(1)}s · {Math.round((s.blob?.size || 0) / 1024)} KB
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-[var(--text-secondary)] mt-3 italic">
                Saved on this device only — never uploaded. Last {MAX_SWINGS} kept.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SwingReview({ swing, onClose, onDeleted }) {
  const videoRef = useRef(null);
  const overlayRef = useRef(null);
  const framesRef = useRef([]); // pose landmarks per sampled frame after analysis
  const url = swing.blob ? URL.createObjectURL(swing.blob) : "";
  const [playing, setPlaying] = useState(false);
  const [tempo, setTempo] = useState({ top: null, impact: null });
  const [analysis, setAnalysis] = useState(null);
  const [analysing, setAnalysing] = useState(false);
  const [analysisError, setAnalysisError] = useState("");
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [sharing, setSharing] = useState(false);

  /** Build a short text summary of the analysis to accompany the shared video. */
  const buildShareText = () => {
    const lines = ["My latest Fairway swing 🏌️"];
    // Tempo first — it's the headline number most people understand.
    if (tempo.top != null && tempo.impact != null && tempo.impact > tempo.top) {
      const ratio = (tempo.top - 0) / Math.max(0.0001, tempo.impact - tempo.top);
      lines.push(`Tempo: ${ratio.toFixed(2)} : 1 (ideal ≈ 3 : 1)`);
    }
    if (Array.isArray(analysis) && analysis.length) {
      const ok = analysis.filter((r) => r.severity === "ok").length;
      const warn = analysis.filter((r) => r.severity === "warn").length;
      const bad = analysis.filter((r) => r.severity === "bad").length;
      lines.push(`AI coach: ✅ ${ok}   ⚠️ ${warn}   ❌ ${bad}`);
      // Worst single thing — useful talking point.
      const worst = analysis.find((r) => r.severity === "bad") || analysis.find((r) => r.severity === "warn");
      if (worst) lines.push(`• ${worst.label}${worst.value ? ` · ${worst.value}` : ""}`);
    }
    lines.push("Recorded in Fairway → https://fairway-golf.org");
    return lines.join("\n");
  };

  /** Share the swing video + a short analysis summary via the native share
   *  sheet. Falls back to downloading the video file when Web Share isn't
   *  available (desktop browsers, some older Androids). */
  const shareSwing = async () => {
    if (!swing?.blob) return;
    setSharing(true);
    try {
      const ext = (swing.mime || "").includes("mp4") ? "mp4" : "webm";
      const createdMs = swing.created_at || 0;
      const dateStr = createdMs
        ? new Date(createdMs).toISOString().slice(0, 10)
        : "today";
      const filename = `fairway-swing-${dateStr}.${ext}`;
      const file = new File([swing.blob], filename, { type: swing.mime || swing.blob.type || "video/webm" });
      const text = buildShareText();

      // Prefer native share-with-files (iOS 15+, Android 12+ Chrome). Falls
      // back automatically if the platform can't share that file type.
      if (typeof navigator !== "undefined" && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ title: "My Fairway swing", text, files: [file] });
        return;
      }

      // No file share — try sharing the text + link only. Still useful so
      // mates can open Fairway via the link, but the video itself needs a
      // separate download.
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ title: "My Fairway swing", text });
        // Also pop a download so the user can attach the video manually if
        // they want — keeps the flow useful even on share-text-only.
        triggerDownload(filename);
        return;
      }

      // Desktop / no share API → straight download + copy text to clipboard.
      triggerDownload(filename);
      if (navigator.clipboard) {
        try { await navigator.clipboard.writeText(text); } catch (_e) { /* no-op */ }
      }
      window.alert("Video downloaded. Caption copied to clipboard — paste it when you share.");
    } catch (err) {
      // AbortError = user cancelled the sheet, which is fine.
      if (err && err.name !== "AbortError") {
        console.warn("SwingLab: share failed", err);
        window.alert("Couldn't share the swing — try downloading and sharing manually.");
      }
    } finally {
      setSharing(false);
    }
  };

  /** Download the current swing blob with a sensible filename. */
  const triggerDownload = (filename) => {
    if (!swing?.blob) return;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(swing.blob);
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoke the URL on next tick so the download has time to start.
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  // Draw the pose skeleton overlay on every video time change. Cheap — runs
  // only when we have analysis frames + the overlay toggle is on.
  useEffect(() => {
    const v = videoRef.current;
    const c = overlayRef.current;
    if (!v || !c) return undefined;
    let frames = framesRef.current;
    if (!frames?.length || !showSkeleton) {
      const ctx0 = c.getContext("2d");
      if (ctx0) ctx0.clearRect(0, 0, c.width, c.height);
      return undefined;
    }
    let raf;
    const draw = async () => {
      const w = v.clientWidth, h = v.clientHeight;
      if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
      const ctx = c.getContext("2d");
      if (!ctx) return;
      ctx.clearRect(0, 0, w, h);
      const { frameNearest, POSE_CONNECTIONS } = await import("../lib/swingAnalysis");
      const f = frameNearest(frames, v.currentTime || 0);
      if (!f?.lms) return;
      // Bones
      ctx.strokeStyle = "#FFE45E";
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      for (const [a, b] of POSE_CONNECTIONS) {
        const p1 = f.lms[a], p2 = f.lms[b];
        if (!p1 || !p2) continue;
        ctx.beginPath();
        ctx.moveTo(p1.x * w, p1.y * h);
        ctx.lineTo(p2.x * w, p2.y * h);
        ctx.stroke();
      }
      // Joints
      ctx.fillStyle = "#C4543C";
      for (let i = 11; i <= 32; i += 1) {
        const p = f.lms[i];
        if (!p) continue;
        ctx.beginPath();
        ctx.arc(p.x * w, p.y * h, 4, 0, Math.PI * 2);
        ctx.fill();
      }
    };
    const onTick = () => { raf = requestAnimationFrame(draw); };
    v.addEventListener("timeupdate", onTick);
    v.addEventListener("seeked", onTick);
    draw();
    return () => {
      v.removeEventListener("timeupdate", onTick);
      v.removeEventListener("seeked", onTick);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [showSkeleton, analysis]);

  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  const playPause = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };

  // Step one frame at the assumed 30 fps. (Browsers can't tell us the exact
  // recorded fps from a MediaRecorder blob — 30 is a sensible default and
  // gives the user a clear "slo-mo" sense regardless of source.)
  const step = (dir) => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    setPlaying(false);
    v.currentTime = Math.max(0, Math.min(v.duration || 0, (v.currentTime || 0) + dir * (1 / 30)));
  };

  const markTop = () => setTempo((t) => ({ ...t, top: videoRef.current?.currentTime ?? 0 }));
  const markImpact = () => setTempo((t) => ({ ...t, impact: videoRef.current?.currentTime ?? 0 }));

  const ratio = (() => {
    if (tempo.top == null || tempo.impact == null) return null;
    const backswing = tempo.top - 0;
    const downswing = tempo.impact - tempo.top;
    if (backswing <= 0 || downswing <= 0) return null;
    return backswing / downswing;
  })();

  return (
    <div data-testid="swing-lab-review">
      <div className="relative rounded-2xl overflow-hidden bg-black aspect-[3/4]">
        <video
          ref={videoRef}
          src={url}
          className="w-full h-full object-contain bg-black"
          playsInline
          onEnded={() => setPlaying(false)}
        />
        <canvas
          ref={overlayRef}
          className="absolute inset-0 pointer-events-none"
          data-testid="swing-lab-skeleton-overlay"
        />
        {analysis && (
          <button
            type="button"
            onClick={() => setShowSkeleton((s) => !s)}
            data-testid="swing-lab-toggle-skeleton"
            className="absolute top-3 right-3 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full"
            style={{
              background: showSkeleton ? "var(--accent-terracotta)" : "rgba(255,255,255,0.85)",
              color: showSkeleton ? "#F2EEE7" : "var(--primary)",
            }}
          >
            Skeleton {showSkeleton ? "On" : "Off"}
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 mt-3">
        <button onClick={() => step(-1)} data-testid="swing-lab-prev-frame" className="btn-secondary flex-1">
          ‹ Frame
        </button>
        <button onClick={playPause} data-testid="swing-lab-play" className="btn-primary flex-1">
          {playing ? <><Pause size={16} weight="fill" /> Pause</> : <><Play size={16} weight="fill" /> Play</>}
        </button>
        <button onClick={() => step(1)} data-testid="swing-lab-next-frame" className="btn-secondary flex-1">
          Frame ›
        </button>
      </div>

      <div className="surface-card-flat p-4 mt-4">
        <div className="flex items-center justify-between mb-2">
          <p className="label-uppercase">Tempo</p>
          {ratio != null && (
            <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--primary)]">
              Pros: 3:1
            </div>
          )}
        </div>
        <p className="text-[11px] text-[var(--text-secondary)] mb-3">
          Scrub to the top of your backswing → tap <strong>Top</strong>. Scrub to impact → tap <strong>Impact</strong>. We&apos;ll work out your tempo ratio.
        </p>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <button onClick={markTop} data-testid="swing-lab-mark-top" className="btn-secondary text-sm">
            {tempo.top == null ? "Mark top" : `Top · ${tempo.top.toFixed(2)}s`}
          </button>
          <button onClick={markImpact} data-testid="swing-lab-mark-impact" className="btn-secondary text-sm">
            {tempo.impact == null ? "Mark impact" : `Impact · ${tempo.impact.toFixed(2)}s`}
          </button>
        </div>
        {ratio != null && (
          <div
            className="rounded-2xl p-3 text-center"
            style={{ background: "var(--accent-sand)" }}
            data-testid="swing-lab-tempo-result"
          >
            <div className="font-mono-num font-black text-3xl text-[var(--primary)]">
              {ratio.toFixed(2)} : 1
            </div>
            <div className="text-[11px] text-[var(--text-secondary)] mt-1">
              Backswing : Downswing — closer to 3 : 1 = smoother tempo
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 mt-4">
        <button onClick={onClose} data-testid="swing-lab-close-review" className="btn-secondary flex-1">
          <ArrowsClockwise size={16} weight="bold" /> Record another
        </button>
        <button
          onClick={shareSwing}
          disabled={sharing || !swing?.blob}
          data-testid="swing-lab-share"
          className="btn-secondary"
          title={analysis ? "Share this analysed swing with mates" : "Share this swing with mates"}
        >
          <Share size={16} weight="bold" />
          <span className="ml-1 hidden sm:inline">{sharing ? "Sharing…" : "Share"}</span>
        </button>
        <button
          onClick={() => { if (window.confirm("Delete this swing?")) onDeleted(); }}
          data-testid="swing-lab-delete"
          className="btn-secondary"
          style={{ borderColor: "var(--accent-terracotta)", color: "var(--accent-terracotta)" }}
        >
          <Trash size={16} weight="bold" />
        </button>
      </div>

      <button
        type="button"
        disabled={analysing}
        data-testid="swing-lab-analyse"
        onClick={async () => {
          if (!videoRef.current) return;
          setAnalysing(true);
          setAnalysisError("");
          try {
            const { analyseSwing } = await import("../lib/swingAnalysis");
            videoRef.current.pause();
            const out = await analyseSwing(videoRef.current);
            framesRef.current = out.frames || [];
            setAnalysis(out.results || []);
            setShowSkeleton(true);
          } catch (e) {
            setAnalysisError(e?.message || "Analysis failed — try again with better lighting.");
          } finally {
            setAnalysing(false);
          }
        }}
        className="btn-primary w-full mt-4"
        style={{ background: "var(--accent-terracotta)" }}
      >
        <Sparkle size={16} weight="fill" /> {analysing ? "Analysing your swing… (takes ~10s)" : "Analyse my swing"}
      </button>

      {analysisError && (
        <div className="text-xs text-[var(--status-bad)] bg-red-50 border border-red-200 rounded-xl px-3 py-2 mt-2">
          {analysisError}
        </div>
      )}

      {analysis && (
        <div className="mt-3 space-y-2" data-testid="swing-lab-analysis-results">
          <p className="label-uppercase">AI coach</p>
          {analysis.map((r) => {
            const bg = r.severity === "ok" ? "var(--accent-turf)"
              : r.severity === "warn" ? "var(--accent-sand)"
              : "rgba(196, 84, 60, 0.12)";
            const color = r.severity === "bad" ? "var(--accent-terracotta)" : "var(--primary)";
            const dot = r.severity === "ok" ? "#16a34a"
              : r.severity === "warn" ? "#D4AF37"
              : "#C4543C";
            return (
              <div
                key={r.id}
                className="rounded-2xl p-3"
                style={{ background: bg }}
                data-testid={`swing-lab-analysis-${r.id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="h-2 w-2 rounded-full" style={{ background: dot }} />
                  <div className="font-heading font-bold text-sm" style={{ color }}>
                    {r.label}{r.value ? ` · ${r.value}` : ""}
                  </div>
                </div>
                <p className="text-xs text-[var(--text)] leading-snug">{r.advice}</p>
              </div>
            );
          })}
          <p className="text-[10px] text-[var(--text-secondary)] italic mt-1">
            AI-guess feedback based on 30 sampled frames — best with side-on, full-body, well-lit footage. Not a replacement for a real lesson.
          </p>

          {/* Prominent post-analysis share CTA — the moment people are most
              likely to want to send their swing to a mate or coach. */}
          <button
            type="button"
            onClick={shareSwing}
            disabled={sharing || !swing?.blob}
            data-testid="swing-lab-share-primary"
            className="btn-primary w-full mt-3"
          >
            <Share size={16} weight="fill" />
            {sharing ? "Opening share sheet…" : "Share this swing with a mate"}
          </button>
        </div>
      )}
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* SetupTipsCard — collapsible "how to frame your phone" guide        */
/* ------------------------------------------------------------------ */
function SetupTipsCard({ onDismiss }) {
  // Two-tab switcher between Down-the-line (recommended for path) and Face-on
  // (recommended for tempo/balance). Each tab shows a hand-drawn SVG diagram
  // of the camera position, plus a do/don't checklist. Dismissable for power
  // users via the X (sticky on this device).
  const [tab, setTab] = useState("dtl"); // dtl | faceon
  // First-open nudge — pulse the "At the driving range" mount option so new
  // users don't miss the floor-tripod guidance. Auto-dismisses after 8s or
  // the first time the user interacts with the card.
  const [nudge, setNudge] = useState(() => {
    try { return localStorage.getItem("swingLabRangeNudgeSeen") !== "1"; }
    catch { return false; }
  });
  useEffect(() => {
    if (!nudge) return;
    const t = setTimeout(() => {
      setNudge(false);
      try { localStorage.setItem("swingLabRangeNudgeSeen", "1"); } catch (_e) { /* no-op */ }
    }, 8000);
    return () => clearTimeout(t);
  }, [nudge]);
  const clearNudge = () => {
    if (!nudge) return;
    setNudge(false);
    try { localStorage.setItem("swingLabRangeNudgeSeen", "1"); } catch (_e) { /* no-op */ }
  };
  return (
    <div
      data-testid="swing-lab-tips"
      onClick={clearNudge}
      className="surface-card p-4 mb-4 border-2 border-[var(--primary)]/15"
    >
      <div className="flex items-start gap-2 mb-3">
        <div className="h-8 w-8 rounded-xl bg-[var(--primary)]/10 text-[var(--primary)] flex items-center justify-center shrink-0">
          <Camera size={18} weight="bold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-black text-[var(--primary)] text-base leading-tight">
            Camera setup
          </div>
          <div className="text-[11px] text-[var(--text-secondary)] mt-0.5">
            Good framing = much better AI feedback. Takes 30 seconds.
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          data-testid="swing-lab-tips-dismiss"
          aria-label="Hide setup tips"
          className="text-[var(--text-secondary)] hover:text-[var(--primary)] text-xl leading-none px-1"
        >
          <X size={16} weight="bold" />
        </button>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 mb-3 rounded-xl bg-[var(--accent-sand)]/60 p-1">
        {[
          { id: "dtl",    label: "Down-the-line",  sub: "best for swing path" },
          { id: "faceon", label: "Face-on",        sub: "best for tempo" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            data-testid={`swing-lab-tips-tab-${t.id}`}
            className={`flex-1 rounded-lg px-2 py-1.5 text-[11px] font-bold transition-all ${
              tab === t.id
                ? "bg-white text-[var(--primary)] shadow-sm"
                : "text-[var(--text-secondary)] hover:text-[var(--primary)]"
            }`}
          >
            <div className="leading-tight">{t.label}</div>
            <div className="text-[9px] font-normal opacity-70 leading-tight normal-case">{t.sub}</div>
          </button>
        ))}
      </div>

      {/* Angle diagram + key dims */}
      <div className="rounded-xl bg-[var(--accent-sand)]/40 p-3 mb-3">
        {tab === "dtl" ? <DownTheLineDiagram /> : <FaceOnDiagram />}
        <div className="grid grid-cols-3 gap-2 mt-3 text-center">
          <DimChip label="Distance" value="8–12 ft" />
          <DimChip label="Height"   value="chest · or floor + tilt" />
          <DimChip label="Phone"    value="landscape" />
        </div>
        {/* Why this angle? — short rationale so people understand */}
        <div className="mt-3 rounded-lg bg-white/70 px-3 py-2 text-[11px] text-[var(--text)] leading-snug" data-testid="swing-lab-tips-why">
          <strong className="text-[var(--primary)]">Why this angle?</strong>{" "}
          {tab === "dtl" ? (
            <>Behind-you / down-the-line keeps the <em>full club arc</em> in shot, so the AI can read shoulder turn, hip slide and head sway. It&apos;s the angle PGA coaches default to.</>
          ) : (
            <>Face-on shows your weight shift and tempo cleanly — best for catching reverse-pivots, balance issues and timing the backswing-to-downswing ratio.</>
          )}
        </div>
      </div>

      {/* Where to put the phone — driving range floor scenario etc. */}
      <div className="rounded-xl border border-[var(--border-default)] bg-white p-3 mb-3" data-testid="swing-lab-tips-mount">
        <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--primary)] mb-1.5">
          Where to put the phone
        </div>
        <ul className="space-y-1.5 text-[11px] text-[var(--text)] leading-snug">
          <li>
            <strong className="text-[var(--primary)]">On the course:</strong> prop it against your golf bag at waist–chest height. A glove or head-cover wedged behind it keeps it steady.
          </li>
          <li className={nudge ? "relative rounded-lg -mx-1 px-1 py-0.5 bg-[var(--accent-terracotta)]/10 ring-2 ring-[var(--accent-terracotta)]/50 animate-pulse" : ""}>
            {nudge && (
              <span
                aria-hidden
                className="absolute -left-3 top-1.5 text-[var(--accent-terracotta)] text-base"
                data-testid="swing-lab-tips-range-arrow"
              >
                ➤
              </span>
            )}
            <strong className="text-[var(--primary)]">At the driving range:</strong> most phones end up on the floor on a mini-tripod or pod — that&apos;s fine, just <strong>tilt the lens up</strong> so the frame catches everything from your <strong>feet to your head + the top of your backswing</strong>. A floor-level shot that crops your hands off is the #1 reason the AI struggles.
          </li>
          <li>
            <strong className="text-[var(--primary)]">No tripod?</strong> Wedge the phone between two club heads on the floor (5-iron + 7-iron works great), or lean it against a range bucket. <em>Don&apos;t</em> place it directly under your swing path — even a small contact will fling it.
          </li>
          <li>
            <strong className="text-[var(--primary)]">Floor-level tip:</strong> step back an extra 2–3 ft compared to the on-bag setup, so the upward tilt still gets your <em>whole body</em> in frame. Less zoom-cropping = much better pose tracking.
          </li>
        </ul>
      </div>

      {/* Do / don't checklist */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div className="rounded-xl border border-[var(--primary)]/15 bg-[var(--primary)]/5 p-2.5">
          <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--primary)] mb-1.5 flex items-center gap-1">
            <Check size={12} weight="bold" /> Do
          </div>
          <ul className="space-y-1 text-[11px] text-[var(--text)] leading-snug">
            <li>• Frame <strong>head + feet + club arc</strong> in shot</li>
            <li>• Bright, even daylight on you (avoid backlight)</li>
            <li>• Plain background — grass, sky, fairway, range mat</li>
            <li>• Prop phone on a tripod / bag / club-head wedge</li>
            <li>• Tilt the lens <strong>up</strong> when it&apos;s on the floor</li>
            <li>• 2–3 practice swings first to warm up</li>
          </ul>
        </div>
        <div className="rounded-xl border border-[var(--accent-terracotta)]/20 bg-[var(--accent-terracotta)]/5 p-2.5">
          <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--accent-terracotta)] mb-1.5 flex items-center gap-1">
            <X size={12} weight="bold" /> Don&apos;t
          </div>
          <ul className="space-y-1 text-[11px] text-[var(--text)] leading-snug">
            <li>• Crop out your feet, head or the top of the backswing</li>
            <li>• Stand against a busy background (people, carts, nets)</li>
            <li>• Film into the sun (silhouettes confuse the AI)</li>
            <li>• Use portrait — landscape gets the full arc</li>
            <li>• Hold the phone — vibration ruins pose tracking</li>
            <li>• Put the phone in your swing path on the floor</li>
          </ul>
        </div>
      </div>

      <p className="text-[10px] text-[var(--text-secondary)] italic mt-2.5 leading-snug">
        Pose detection runs on-device — nothing is uploaded. The clearer the framing,
        the better the skeleton overlay and the fault-detection chips after Analyse.
      </p>
    </div>
  );
}

function DimChip({ label, value }) {
  return (
    <div className="rounded-lg bg-white border border-[var(--border-default)] py-1.5 px-2">
      <div className="text-[9px] uppercase tracking-wider font-bold text-[var(--text-secondary)] leading-none">
        {label}
      </div>
      <div className="font-heading font-black text-[var(--primary)] text-sm leading-tight">
        {value}
      </div>
    </div>
  );
}

/* Down-the-line: camera sits behind golfer in line with target.
 * SVG drawn as a simple top-down stick figure + camera + dashed target line.
 */
function DownTheLineDiagram() {
  return (
    <svg viewBox="0 0 320 110" className="w-full h-auto" role="img" aria-label="Down-the-line camera position">
      {/* Target line */}
      <line x1="20" y1="55" x2="300" y2="55" stroke="#A3946F" strokeWidth="1.5" strokeDasharray="4 4" />
      <text x="290" y="48" fontSize="9" fill="#A3946F" textAnchor="end" fontFamily="system-ui">target →</text>

      {/* Camera (behind golfer) */}
      <g transform="translate(40, 40)">
        <rect x="0" y="0" width="26" height="18" rx="3" fill="#2A4A33" />
        <circle cx="13" cy="9" r="5" fill="#0F1F17" />
        <circle cx="13" cy="9" r="2.5" fill="#C0C0C0" />
        <text x="13" y="34" fontSize="8" fill="#2A4A33" textAnchor="middle" fontWeight="700" fontFamily="system-ui">CAMERA</text>
      </g>

      {/* Distance bracket */}
      <line x1="70" y1="68" x2="155" y2="68" stroke="#A3946F" strokeWidth="0.8" />
      <line x1="70" y1="65" x2="70" y2="71" stroke="#A3946F" strokeWidth="0.8" />
      <line x1="155" y1="65" x2="155" y2="71" stroke="#A3946F" strokeWidth="0.8" />
      <text x="112" y="80" fontSize="8" fill="#5B6B5F" textAnchor="middle" fontFamily="system-ui">~8–10 ft</text>

      {/* Golfer stick figure (side-on, viewed from behind) */}
      <g transform="translate(155, 22)">
        {/* head */}
        <circle cx="0" cy="0" r="7" fill="none" stroke="#2A4A33" strokeWidth="2" />
        {/* body */}
        <line x1="0" y1="7" x2="0" y2="30" stroke="#2A4A33" strokeWidth="2" />
        {/* arms holding club */}
        <line x1="0" y1="14" x2="-8" y2="22" stroke="#2A4A33" strokeWidth="2" />
        <line x1="-8" y1="22" x2="14" y2="40" stroke="#C0461E" strokeWidth="2" />
        {/* legs */}
        <line x1="0" y1="30" x2="-5" y2="44" stroke="#2A4A33" strokeWidth="2" />
        <line x1="0" y1="30" x2="5"  y2="44" stroke="#2A4A33" strokeWidth="2" />
        {/* ball */}
        <circle cx="14" cy="42" r="2.5" fill="#fff" stroke="#2A4A33" strokeWidth="1" />
        <text x="0" y="58" fontSize="8" fill="#2A4A33" textAnchor="middle" fontWeight="700" fontFamily="system-ui">YOU</text>
      </g>

      {/* Flag at target */}
      <g transform="translate(280, 25)">
        <line x1="0" y1="0" x2="0" y2="30" stroke="#2A4A33" strokeWidth="1.5" />
        <polygon points="0,0 10,4 0,8" fill="#C0461E" />
      </g>
    </svg>
  );
}

/* Face-on: camera perpendicular to target line, level with golfer. */
function FaceOnDiagram() {
  return (
    <svg viewBox="0 0 320 110" className="w-full h-auto" role="img" aria-label="Face-on camera position">
      {/* Target line */}
      <line x1="40" y1="55" x2="300" y2="55" stroke="#A3946F" strokeWidth="1.5" strokeDasharray="4 4" />
      <text x="290" y="48" fontSize="9" fill="#A3946F" textAnchor="end" fontFamily="system-ui">target →</text>

      {/* Golfer (face-on, in middle) */}
      <g transform="translate(155, 22)">
        <circle cx="0" cy="0" r="7" fill="none" stroke="#2A4A33" strokeWidth="2" />
        <line x1="0" y1="7" x2="0" y2="30" stroke="#2A4A33" strokeWidth="2" />
        <line x1="-9" y1="14" x2="9" y2="14" stroke="#2A4A33" strokeWidth="2" />
        <line x1="9" y1="14" x2="18" y2="34" stroke="#C0461E" strokeWidth="2" />
        <line x1="0" y1="30" x2="-6" y2="44" stroke="#2A4A33" strokeWidth="2" />
        <line x1="0" y1="30" x2="6"  y2="44" stroke="#2A4A33" strokeWidth="2" />
        <circle cx="14" cy="46" r="2.5" fill="#fff" stroke="#2A4A33" strokeWidth="1" />
        <text x="0" y="58" fontSize="8" fill="#2A4A33" textAnchor="middle" fontWeight="700" fontFamily="system-ui">YOU</text>
      </g>

      {/* Camera below golfer (perpendicular to target line) */}
      <g transform="translate(142, 88)">
        <rect x="0" y="0" width="26" height="18" rx="3" fill="#2A4A33" />
        <circle cx="13" cy="9" r="5" fill="#0F1F17" />
        <circle cx="13" cy="9" r="2.5" fill="#C0C0C0" />
      </g>
      {/* Arrow from camera up to golfer */}
      <line x1="155" y1="85" x2="155" y2="72" stroke="#5B6B5F" strokeWidth="0.8" markerEnd="url(#arr)" />
      <defs>
        <marker id="arr" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
          <polygon points="0 0, 6 3, 0 6" fill="#5B6B5F" />
        </marker>
      </defs>
      <text x="200" y="100" fontSize="8" fill="#5B6B5F" fontFamily="system-ui">camera ~8 ft, chest height</text>
    </svg>
  );
}


/* ------------------------------------------------------------------ */
/* RangeTipPill — small "📱 At the range?" pill on the recording view */
/* Tap to expand a contextual range-specific tip; auto-collapses 6s.  */
/* ------------------------------------------------------------------ */
function RangeTipPill() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => setOpen(false), 6000);
    return () => clearTimeout(t);
  }, [open]);
  return (
    <div className="absolute top-3 right-3 max-w-[70%]">
      {open ? (
        <button
          type="button"
          onClick={() => setOpen(false)}
          data-testid="swing-lab-range-tip-open"
          className="text-left bg-black/70 backdrop-blur-sm text-white text-[11px] leading-snug rounded-2xl px-3 py-2 shadow-lg"
        >
          <div className="flex items-center gap-1 mb-0.5">
            <Camera size={12} weight="bold" />
            <span className="font-bold uppercase tracking-wider text-[10px]">Range tip</span>
          </div>
          <div className="opacity-95">
            Phone on the floor? Tilt the lens <strong className="text-white">up</strong> and step back <strong className="text-white">2–3 ft extra</strong> so feet + head + top-of-backswing all stay in frame.
          </div>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="swing-lab-range-tip-pill"
          className="inline-flex items-center gap-1 bg-black/55 backdrop-blur-sm text-white text-[11px] font-bold px-2.5 py-1 rounded-full hover:bg-black/70 transition-colors"
        >
          <Camera size={12} weight="bold" />
          <span>At the range?</span>
        </button>
      )}
    </div>
  );
}
